/**
 * ChildSchedule.js - Admin Student Schedule Management
 * 
 * Grid view showing students assigned to rooms by day.
 * Features:
 * - Week navigation
 * - Room rows with students listed
 * - Add/remove students from rooms
 * - Apply default schedule button
 * - Visit indicators
 * - Custom hours display
 * - Print functionality
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import dataStore from '../data/dataStore';

function ChildSchedule() {
  const { theme, colors, darkMode } = useTheme();
  const { user: currentUser } = useAuth();
  
  // Data state
  const [rooms, setRooms] = useState([]);
  const [children, setChildren] = useState([]);
  const [childSchedule, setChildSchedule] = useState({});
  const [weekDates, setWeekDates] = useState([]);
  const [currentMonday, setCurrentMonday] = useState(null);
  
  // Modal state
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedCell, setSelectedCell] = useState({ roomId: null, dateStr: null });
  const [selectedChildren, setSelectedChildren] = useState([]);
  
  // UI state
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [hoveredCell, setHoveredCell] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  
  // Drag and drop state
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverCell, setDragOverCell] = useState(null);

  // Get Monday of current week
  const getMonday = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    d.setDate(diff);
    d.setHours(12, 0, 0, 0);
    return d;
  };

  // Initialize week dates
  const initializeWeek = useCallback((monday) => {
    const dates = [];
    for (let i = 0; i < 5; i++) {
      const date = new Date(monday);
      date.setDate(monday.getDate() + i);
      dates.push(date);
    }
    setWeekDates(dates);
    setCurrentMonday(monday);
  }, []);

  // Load data
  const loadData = useCallback(() => {
    // Filter rooms to only show those with showOnStudentSchedule enabled
    const loadedRooms = dataStore.getRooms().filter(r => r.showOnStudentSchedule !== false);
    const loadedChildren = dataStore.getActiveChildren();
    setRooms(loadedRooms);
    setChildren(loadedChildren);
    if (currentMonday) {
      const mondayStr = currentMonday.toISOString().split('T')[0];
      const loadedSchedule = dataStore.getChildScheduleForWeek(mondayStr);
      setChildSchedule(loadedSchedule);
      // Debug logging
      console.log('ChildSchedule loadData:', {
        rooms: loadedRooms.length,
        children: loadedChildren.length,
        childrenData: loadedChildren,
        schedule: loadedSchedule,
        mondayStr
      });
    }
  }, [currentMonday]);

  // Initialize on mount
  useEffect(() => {
    const today = new Date();
    const monday = getMonday(today);
    initializeWeek(monday);
  }, [initializeWeek]);

  // Load data when week changes
  useEffect(() => {
    if (currentMonday) {
      loadData();
    }
  }, [currentMonday, loadData]);

  // Clear messages
  useEffect(() => {
    if (message || error) {
      const timer = setTimeout(() => { setMessage(''); setError(''); }, 3000);
      return () => clearTimeout(timer);
    }
  }, [message, error]);

  // Navigation
  const goToPreviousWeek = () => {
    const newMonday = new Date(currentMonday);
    newMonday.setDate(currentMonday.getDate() - 7);
    initializeWeek(newMonday);
  };

  const goToNextWeek = () => {
    const newMonday = new Date(currentMonday);
    newMonday.setDate(currentMonday.getDate() + 7);
    initializeWeek(newMonday);
  };

  const goToCurrentWeek = () => {
    const monday = getMonday(new Date());
    initializeWeek(monday);
  };

  // Format date
  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatDayName = (date) => {
    return date.toLocaleDateString('en-US', { weekday: 'long' });
  };

  const dateToString = (date) => {
    return date.toISOString().split('T')[0];
  };

  // Format time for display
  const formatTime = (time) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${minutes}${ampm}`;
  };

  // Get children in a cell
  const getChildrenInCell = (roomId, dateStr) => {
    const daySchedule = childSchedule[dateStr] || {};
    const result = [];
    for (const childId in daySchedule) {
      if (daySchedule[childId].roomId === roomId) {
        const child = children.find(c => c.id === childId);
        if (child) {
          result.push({ child, entry: daySchedule[childId] });
        }
      }
    }
    // Sort by age (youngest first)
    result.sort((a, b) => {
      const ageA = dataStore.calculateChildAge(a.child.dateOfBirth);
      const ageB = dataStore.calculateChildAge(b.child.dateOfBirth);
      const monthsA = ageA ? ageA.months : 0;
      const monthsB = ageB ? ageB.months : 0;
      return monthsA - monthsB;
    });
    return result;
  };

  // Get week range for print header
  const getWeekRangeForPrint = () => {
    if (weekDates.length < 5) return '';
    const startDate = weekDates[0].toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    const endDate = weekDates[4].toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    return `${startDate} - ${endDate}`;
  };

  // Open assign modal
  const openAssignModal = (roomId, dateStr) => {
    setSelectedCell({ roomId, dateStr });
    const currentChildren = getChildrenInCell(roomId, dateStr).map(c => c.child.id);
    setSelectedChildren(currentChildren);
    setShowAssignModal(true);
  };

  // Toggle child selection
  const toggleChildSelection = (childId) => {
    setSelectedChildren(prev => 
      prev.includes(childId) 
        ? prev.filter(id => id !== childId)
        : [...prev, childId]
    );
  };

  // Save assignments
  const saveAssignments = async () => {
    const { roomId, dateStr } = selectedCell;
    const currentChildren = getChildrenInCell(roomId, dateStr).map(c => c.child.id);
    const dayCode = dataStore.getDayCode(dateStr);
    const targetDate = new Date(dateStr + 'T12:00:00');
    const dayName = targetDate.toLocaleDateString('en-US', { weekday: 'long' });
    
    // Check for children being added who aren't scheduled for this day
    const newChildren = selectedChildren.filter(id => !currentChildren.includes(id));
    const unscheduledNewChildren = newChildren.filter(childId => {
      const child = children.find(c => c.id === childId);
      return !child?.scheduledDays?.includes(dayCode);
    });
    
    if (unscheduledNewChildren.length > 0) {
      const names = unscheduledNewChildren.map(id => {
        const child = children.find(c => c.id === id);
        return child?.firstName;
      }).join(', ');
      
      if (!window.confirm(`${dayName} is not a Primary Day for: ${names}.\n\nDo you want to override?`)) {
        return;
      }
    }
    
    // Remove children no longer selected
    for (const childId of currentChildren) {
      if (!selectedChildren.includes(childId)) {
        await dataStore.removeChildFromDay(childId, dateStr, currentUser);
      }
    }
    
    // Add newly selected children
    for (const childId of selectedChildren) {
      if (!currentChildren.includes(childId)) {
        const child = children.find(c => c.id === childId);
        const isVisit = child && child.primaryRoomId !== roomId;
        const visitNote = isVisit ? `Visiting from ${rooms.find(r => r.id === child.primaryRoomId)?.name || 'another room'}` : null;
        const startTime = child?.customHoursStart || '08:00';
        const endTime = child?.customHoursEnd || '17:00';
        await dataStore.assignChildToRoom(childId, roomId, dateStr, { isVisit, visitNote, startTime, endTime }, currentUser);
      }
    }
    
    setShowAssignModal(false);
    loadData();
    setMessage('Schedule updated');
  };

  // Toggle absent status for a child in a cell
  const toggleChildAbsent = async (childId, dateStr) => {
    try {
      await dataStore.toggleChildAbsent(childId, dateStr, currentUser);
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  // Apply default schedule for the week
  const applyDefaultSchedule = async () => {
    if (!currentMonday) return;
    
    const startDate = dateToString(weekDates[0]);
    const endDate = dateToString(weekDates[4]);
    
    if (window.confirm(`Apply default schedule for all active students from ${startDate} to ${endDate}?\n\nThis will add students to their primary rooms based on their scheduled days. Existing assignments will not be changed.`)) {
      setIsLoading(true);
      setLoadingMessage('Applying default schedule...');
      try {
        const result = await dataStore.applyDefaultSchedule(null, startDate, endDate, false, currentUser);
        loadData();
        setMessage(`Applied schedule: ${result.added} assignments added, ${result.skipped} already existed`);
      } catch (err) {
        setError('Error applying schedule: ' + err.message);
      } finally {
        setIsLoading(false);
        setLoadingMessage('');
      }
    }
  };

  // Clear all assignments for the week
  const clearWeekSchedule = async () => {
    if (!currentMonday) return;
    
    const startDate = dateToString(weekDates[0]);
    const endDate = dateToString(weekDates[4]);
    
    if (window.confirm(`Clear ALL student assignments from ${startDate} to ${endDate}?\n\nThis cannot be undone.`)) {
      setIsLoading(true);
      setLoadingMessage('Clearing schedule...');
      try {
        // Use optimized single-call range delete
        const result = await dataStore.clearChildScheduleForDateRange(startDate, endDate, currentUser);
        loadData();
        setMessage(`Week schedule cleared (${result.cleared} assignments removed)`);
      } catch (err) {
        setError('Error clearing schedule: ' + err.message);
      } finally {
        setIsLoading(false);
        setLoadingMessage('');
      }
    }
  };

  // Get week range string
  const getWeekRangeString = () => {
    if (weekDates.length < 5) return '';
    return `${formatDate(weekDates[0])} - ${formatDate(weekDates[4])}`;
  };

  // Check if current week
  const isCurrentWeek = () => {
    if (!currentMonday) return false;
    const thisMonday = getMonday(new Date());
    return currentMonday.toDateString() === thisMonday.toDateString();
  };

  // Drag and drop handlers
  const handleDragStart = (e, childId, sourceRoomId, dateStr) => {
    setDraggedItem({ childId, sourceRoomId, dateStr });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', childId);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDragOverCell(null);
  };

  const handleDragOver = (e, roomId, dateStr) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const cellKey = `${roomId}-${dateStr}`;
    if (dragOverCell !== cellKey) {
      setDragOverCell(cellKey);
    }
  };

  const handleDragLeave = () => {
    setDragOverCell(null);
  };

  const handleDrop = async (e, targetRoomId, dateStr) => {
    e.preventDefault();
    setDragOverCell(null);
    
    if (!draggedItem) return;
    
    const { childId, sourceRoomId, dateStr: sourceDateStr } = draggedItem;
    
    // Don't do anything if dropping in same cell
    if (sourceRoomId === targetRoomId && sourceDateStr === dateStr) {
      setDraggedItem(null);
      return;
    }
    
    const child = children.find(c => c.id === childId);
    
    // If different day, check if child is scheduled for that day
    if (sourceDateStr !== dateStr) {
      const targetDayCode = dataStore.getDayCode(dateStr);
      if (!child?.scheduledDays?.includes(targetDayCode)) {
        const targetDate = new Date(dateStr + 'T12:00:00');
        const dayName = targetDate.toLocaleDateString('en-US', { weekday: 'long' });
        
        if (!window.confirm(`${dayName} is not one of ${child?.firstName}'s Primary Days.\n\nDo you want to override?`)) {
          setDraggedItem(null);
          return;
        }
      }
    }
    
    const isVisit = child && child.primaryRoomId !== targetRoomId;
    const visitNote = isVisit ? `Visiting from ${rooms.find(r => r.id === child.primaryRoomId)?.name || 'another room'}` : null;
    
    // Preserve existing times or use child's default times
    const existingEntry = childSchedule[sourceDateStr]?.[childId];
    const startTime = existingEntry?.startTime || child?.customHoursStart || '08:00';
    const endTime = existingEntry?.endTime || child?.customHoursEnd || '17:00';
    
    await dataStore.assignChildToRoom(childId, targetRoomId, dateStr, { isVisit, visitNote, startTime, endTime }, currentUser);
    loadData();
    
    setDraggedItem(null);
  };


  // STYLES
  const containerStyle = { padding: '20px', maxWidth: '1400px', margin: '0 auto' };
  const headerCardStyle = { backgroundColor: theme.cardBackground, padding: '16px 20px', borderRadius: '8px', border: `1px solid ${theme.border}`, marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' };
  const titleStyle = { color: theme.text, margin: 0, fontSize: '1.4rem', fontWeight: '700' };
  const buttonStyle = { padding: '8px 16px', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: '600', transition: 'all 0.2s' };
  const primaryButtonStyle = { ...buttonStyle, backgroundColor: colors.green, color: '#333' };
  const secondaryButtonStyle = { ...buttonStyle, backgroundColor: theme.backgroundSecondary, color: theme.text, border: `1px solid ${theme.border}` };
  
  const navCardStyle = { backgroundColor: theme.cardBackground, padding: '12px 20px', borderRadius: '8px', border: `1px solid ${theme.border}`, marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' };
  const navButtonStyle = { ...buttonStyle, backgroundColor: colors.blue, color: '#333' };
  const weekLabelStyle = { fontSize: '1rem', fontWeight: '600', color: theme.text };

  const gridContainerStyle = { backgroundColor: theme.cardBackground, borderRadius: '12px', border: `1px solid ${theme.border}`, overflow: 'hidden' };
  const gridStyle = { display: 'grid', gridTemplateColumns: '140px repeat(5, 1fr)', gap: '1px', backgroundColor: theme.border };
  
  const headerCellStyle = { padding: '12px 8px', backgroundColor: theme.backgroundSecondary, textAlign: 'center' };
  const roomHeaderStyle = { padding: '12px 8px', backgroundColor: colors.blue, color: darkMode ? '#fff' : '#333', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '600', fontSize: '0.95rem' };
  const dayNameStyle = { fontSize: '0.95rem', fontWeight: '600', color: theme.text };
  const dayDateStyle = { fontSize: '0.8rem', color: theme.textSecondary, marginTop: '2px' };

  const roomLabelStyle = { padding: '10px', backgroundColor: theme.backgroundSecondary, display: 'flex', alignItems: 'center', justifyContent: 'center' };
  const roomCardColors = [darkMode ? '#3d5a4a' : '#e8f5e9', darkMode ? '#4a5a3d' : '#f1f8e9', darkMode ? '#3d4a5a' : '#e3f2fd', darkMode ? '#5a4a3d' : '#fff8e1'];
  const roomNameStyle = { fontSize: '0.85rem', fontWeight: '600', color: theme.text };
  const roomInfoStyle = { fontSize: '0.7rem', color: theme.textSecondary, marginTop: '2px' };

  const cellStyle = (roomId, dateStr) => ({
    padding: '8px', 
    backgroundColor: dragOverCell === `${roomId}-${dateStr}` 
      ? (darkMode ? 'rgba(119,184,219,0.3)' : 'rgba(119,184,219,0.2)') 
      : theme.cardBackground, 
    minHeight: '100px', 
    display: 'flex', 
    flexDirection: 'column', 
    gap: '4px', 
    position: 'relative',
    transition: 'background-color 0.15s ease',
    border: dragOverCell === `${roomId}-${dateStr}` ? `2px dashed ${colors.blue}` : '2px solid transparent'
  });
  
  const childTagStyle = (isVisit, isDragging, isAbsent, isExtraDay) => {
    // Determine background color based on state
    let backgroundColor;
    if (isAbsent) {
      backgroundColor = darkMode ? 'rgba(239,83,80,0.4)' : 'rgba(239,83,80,0.3)';
    } else if (isExtraDay) {
      // Light blue for students scheduled on a non-primary day
      backgroundColor = darkMode ? 'rgba(119,184,219,0.6)' : 'rgba(119,184,219,0.5)';
    } else if (isVisit) {
      backgroundColor = colors.orange;
    } else {
      backgroundColor = colors.green;
    }
    
    return {
      display: 'flex', flexDirection: 'column', padding: '6px 8px', borderRadius: '4px', fontSize: '0.85rem', fontWeight: '500',
      backgroundColor,
      color: isAbsent ? (darkMode ? '#fff' : '#333') : '#333', 
      gap: '2px', position: 'relative',
      cursor: 'grab', opacity: isDragging ? 0.5 : 1, userSelect: 'none'
    };
  };

  const removeButtonStyle = { 
    position: 'absolute', top: '2px', right: '4px',
    background: 'none', border: 'none', cursor: 'pointer', opacity: 0.8, 
    padding: '2px 6px', fontSize: '1.4rem', fontWeight: 'bold', color: 'inherit',
    lineHeight: 1
  };

  const customHoursStyle = { fontSize: '0.8rem', opacity: 0.9 };

  const addButtonBaseStyle = {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
    padding: '8px 16px', border: `1px dashed ${theme.border}`, borderRadius: '6px',
    backgroundColor: 'transparent', color: theme.textSecondary, cursor: 'pointer', fontSize: '0.85rem', fontWeight: '500', transition: 'all 0.2s', width: '100%'
  };

  const addButtonHoverStyle = {
    ...addButtonBaseStyle,
    border: `1px dashed ${colors.blue}`,
    backgroundColor: darkMode ? 'rgba(119,184,219,0.15)' : 'rgba(119,184,219,0.1)',
    color: colors.blue
  };

  const messageStyle = { padding: '10px 16px', borderRadius: '6px', marginBottom: '16px', textAlign: 'center', fontWeight: '500' };

  const modalOverlayStyle = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
  const modalStyle = { backgroundColor: theme.cardBackground, borderRadius: '12px', padding: '20px', maxWidth: '450px', width: '95%', maxHeight: '80vh', overflow: 'auto', boxShadow: '0 4px 20px rgba(0,0,0,0.2)' };
  const modalTitleStyle = { fontSize: '1.1rem', fontWeight: '600', color: theme.text, marginBottom: '16px', paddingBottom: '10px', borderBottom: `1px solid ${theme.border}` };
  const childListStyle = { maxHeight: '300px', overflowY: 'auto' };
  const childOptionStyle = (isSelected) => ({
    display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', borderRadius: '6px', cursor: 'pointer', marginBottom: '6px',
    backgroundColor: isSelected ? (darkMode ? 'rgba(119,184,219,0.2)' : 'rgba(119,184,219,0.15)') : theme.backgroundSecondary,
    border: isSelected ? `2px solid ${colors.blue}` : `1px solid ${theme.border}`
  });
  const checkboxStyle = { width: '18px', height: '18px', accentColor: colors.blue };
  const modalButtonRowStyle = { display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '16px', paddingTop: '12px', borderTop: `1px solid ${theme.border}` };


  // RENDER
  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={headerCardStyle} className="no-print">
        <h1 style={titleStyle}>📅 Student Schedule</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          {/* Legend */}
          <div style={{ display: 'flex', gap: '16px', fontSize: '0.85rem', color: theme.text }}>
            <span><span style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: colors.green, borderRadius: '3px', marginRight: '6px', verticalAlign: 'middle' }}></span>Regular</span>
            <span><span style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: colors.orange, borderRadius: '3px', marginRight: '6px', verticalAlign: 'middle' }}></span>Visiting</span>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button 
              style={{ ...primaryButtonStyle, backgroundColor: colors.red, color: '#333', opacity: isLoading ? 0.6 : 1 }} 
              onClick={clearWeekSchedule}
              disabled={isLoading}
            >
              Clear All
            </button>
            <button 
              style={{ ...primaryButtonStyle, opacity: isLoading ? 0.6 : 1 }} 
              onClick={applyDefaultSchedule}
              disabled={isLoading}
            >
              Apply Default Schedule
            </button>
          </div>
        </div>
      </div>

      {/* Messages */}
      {message && <div className="no-print" style={{ ...messageStyle, backgroundColor: colors.green, color: darkMode ? '#fff' : '#333' }}>{message}</div>}
      {error && <div className="no-print" style={{ ...messageStyle, backgroundColor: colors.red, color: darkMode ? '#fff' : '#333' }}>{error}</div>}

      {/* Week Navigation */}
      <div style={navCardStyle} className="no-print">
        <button style={navButtonStyle} onClick={goToPreviousWeek}>← Previous</button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={weekLabelStyle}>{getWeekRangeString()}</span>
          {!isCurrentWeek() && (
            <button style={secondaryButtonStyle} onClick={goToCurrentWeek}>Today</button>
          )}
        </div>
        <button style={navButtonStyle} onClick={goToNextWeek}>Next →</button>
      </div>

      {/* Schedule Grid - Print Area */}
      <div className="child-schedule-print-area">
        {/* Print Header */}
        <div className="print-header" style={{ display: 'none' }}>
          <div className="print-title">Student Schedule</div>
          <div className="print-date-range">{getWeekRangeForPrint()}</div>
        </div>
        
        <div style={gridContainerStyle}>
          <div style={gridStyle} className="print-grid">
          {/* Header Row */}
          <div style={roomHeaderStyle} className="print-header-cell">Room</div>
          {weekDates.map(date => (
            <div key={dateToString(date)} style={headerCellStyle} className="print-header-cell">
              <div style={dayNameStyle}>{formatDayName(date)}</div>
              <div style={dayDateStyle}>{formatDate(date)}</div>
            </div>
          ))}

          {/* Room Rows */}
          {rooms.map((room, roomIndex) => (
            <React.Fragment key={room.id}>
              {/* Room Label */}
              <div style={roomLabelStyle} className="print-room-cell">
                <div style={{ padding: '8px 10px', backgroundColor: roomCardColors[roomIndex % roomCardColors.length], borderRadius: '6px', textAlign: 'center', width: '100%' }}>
                  <div style={roomNameStyle}>🏫 {room.shortName}</div>
                  {(room.ratio || room.ageRange) && (
                    <div style={roomInfoStyle}>
                      {room.ratio ? `${room.ratio}:1` : ''}{room.ratio && room.ageRange ? ' • ' : ''}{room.ageRange ? room.ageRange.displayText : ''}
                    </div>
                  )}
                </div>
              </div>

              {/* Day Cells */}
              {weekDates.map(date => {
                const dateStr = dateToString(date);
                const cellChildren = getChildrenInCell(room.id, dateStr);
                
                return (
                  <div 
                    key={`${room.id}-${dateStr}`} 
                    style={cellStyle(room.id, dateStr)}
                    onDragOver={(e) => handleDragOver(e, room.id, dateStr)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, room.id, dateStr)}
                  >
                    {/* Ratio Warning Badge - only shows when over ratio */}
                    {(() => {
                      const ratioStatus = dataStore.calculateRoomRatio(room.id, dateStr);
                      if (ratioStatus && !ratioStatus.noRatioSet && !ratioStatus.isValid) {
                        return (
                          <div 
                            className="no-print"
                            style={{
                              marginBottom: '6px',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '0.75rem',
                              fontWeight: '600',
                              textAlign: 'center',
                              backgroundColor: darkMode ? 'rgba(239,83,80,0.3)' : 'rgba(239,83,80,0.4)',
                              color: darkMode ? '#fff' : '#333',
                              border: `1px solid ${colors.red}`
                            }}
                            title={`RATIO EXCEEDED: ${ratioStatus.childCount} children with ${ratioStatus.teacherCount} teacher(s). Room requires ${room.ratio}:1 ratio`}
                          >
                            👶 {ratioStatus.childCount}
                          </div>
                        );
                      }
                      return null;
                    })()}

                    {/* Students List */}
                    {cellChildren.map(({ child, entry }) => {
                      const isDragging = draggedItem?.childId === child.id && draggedItem?.dateStr === dateStr;
                      const age = dataStore.calculateChildAge(child.dateOfBirth);
                      const primaryRoom = dataStore.getRoomById(child.primaryRoomId);
                      // Check if this is an "extra day" - not one of their scheduled days
                      const dayCode = dataStore.getDayCode(dateStr);
                      const isExtraDay = !child.scheduledDays?.includes(dayCode);
                      return (
                        <div 
                          key={child.id} 
                          style={childTagStyle(entry.isVisit, isDragging, entry.isAbsent, isExtraDay)} 
                          title={entry.isAbsent ? `${child.firstName} ${child.lastName} - Absent` : (isExtraDay ? `${child.firstName} ${child.lastName} - Extra Day (not scheduled)` : (entry.isVisit ? entry.visitNote : `${child.firstName} ${child.lastName} - Drag to move`))}
                          draggable={!entry.isAbsent}
                          onDragStart={(e) => !entry.isAbsent && handleDragStart(e, child.id, room.id, dateStr)}
                          onDragEnd={handleDragEnd}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', paddingRight: '20px' }}>
                            {isExtraDay && !entry.isAbsent && <span>📅</span>}
                            {entry.isVisit && !isExtraDay && !entry.isAbsent && <span>↗</span>}
                            <span>{child.firstName} {child.lastName}{entry.isAbsent && ' (Absent)'}</span>
                          </div>
                          <div style={customHoursStyle}>
                            {age?.displayText || '?'} • {primaryRoom?.shortName || '?'}
                            {child.useCustomHours && ` • ${formatTime(child.customHoursStart)}-${formatTime(child.customHoursEnd)}`}
                          </div>
                          <button 
                            style={{
                              ...removeButtonStyle,
                              color: entry.isAbsent ? colors.green : 'inherit',
                              fontSize: entry.isAbsent ? '1rem' : '1.4rem'
                            }} 
                            className="no-print" 
                            onClick={(e) => { e.stopPropagation(); toggleChildAbsent(child.id, dateStr); }} 
                            title={entry.isAbsent ? "Mark Present" : "Mark Absent"}
                          >
                            {entry.isAbsent ? '✓' : '×'}
                          </button>
                        </div>
                      );
                    })}
                    
                    {/* Assign Button - at bottom */}
                    <button 
                      className="no-print"
                      style={hoveredCell === `${room.id}-${dateStr}` ? addButtonHoverStyle : addButtonBaseStyle}
                      onClick={() => openAssignModal(room.id, dateStr)}
                      onMouseEnter={() => setHoveredCell(`${room.id}-${dateStr}`)}
                      onMouseLeave={() => setHoveredCell(null)}
                    >
                      + Assign
                    </button>
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>
      </div> {/* End print area */}

      {/* Assign Students Modal */}
      {showAssignModal && (
        <div style={modalOverlayStyle} onClick={() => setShowAssignModal(false)}>
          <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
            <h2 style={modalTitleStyle}>
              📋 Assign Students - {rooms.find(r => r.id === selectedCell.roomId)?.name} ({selectedCell.dateStr})
            </h2>
            
            <div style={{ marginBottom: '12px', fontSize: '0.85rem', color: theme.textSecondary }}>
              Select students to assign to this room on this day:
            </div>
            
            <div style={childListStyle}>
              {(() => {
                // Get day code for the selected date
                const dayCode = dataStore.getDayCode(selectedCell.dateStr);
                
                // Filter and sort children
                const primaryRoomChildren = children.filter(child => 
                  child.primaryRoomId === selectedCell.roomId && 
                  child.scheduledDays?.includes(dayCode)
                );
                const visitingChildren = children.filter(child => 
                  child.primaryRoomId !== selectedCell.roomId && 
                  child.scheduledDays?.includes(dayCode)
                );
                const unscheduledChildren = children.filter(child => 
                  !child.scheduledDays?.includes(dayCode)
                );
                
                if (primaryRoomChildren.length === 0 && visitingChildren.length === 0 && unscheduledChildren.length === 0) {
                  return (
                    <div style={{ padding: '20px', textAlign: 'center', color: theme.textSecondary }}>
                      No active students. Enroll students first.
                    </div>
                  );
                }
                
                const renderChild = (child, isVisitor = false, isUnscheduled = false) => {
                  const isSelected = selectedChildren.includes(child.id);
                  const age = dataStore.calculateChildAge(child.dateOfBirth);
                  const room = rooms.find(r => r.id === child.primaryRoomId);
                  
                  return (
                    <div key={child.id} style={{
                      ...childOptionStyle(isSelected),
                      opacity: isUnscheduled ? 0.5 : 1
                    }} onClick={() => toggleChildSelection(child.id)}>
                      <input type="checkbox" checked={isSelected} onChange={() => {}} style={checkboxStyle} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '600', color: theme.text }}>
                          {child.firstName} {child.lastName}
                          {isVisitor && <span style={{ marginLeft: '6px', fontSize: '0.7rem', color: theme.accent }}>👋 Visitor</span>}
                          {isUnscheduled && <span style={{ marginLeft: '6px', fontSize: '0.7rem', color: theme.textSecondary }}>(not scheduled this day)</span>}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: theme.textSecondary }}>
                          {age?.displayText} • {room?.shortName || 'No room'}
                          {child.customHoursStart && ` • ${formatTime(child.customHoursStart)}-${formatTime(child.customHoursEnd)}`}
                        </div>
                      </div>
                    </div>
                  );
                };
                
                return (
                  <>
                    {primaryRoomChildren.length > 0 && (
                      <>
                        <div style={{ padding: '8px 12px', backgroundColor: theme.hover, fontWeight: '600', fontSize: '0.8rem', color: theme.textSecondary }}>
                          Primary Room Students ({primaryRoomChildren.length})
                        </div>
                        {primaryRoomChildren.map(child => renderChild(child, false, false))}
                      </>
                    )}
                    {visitingChildren.length > 0 && (
                      <>
                        <div style={{ padding: '8px 12px', backgroundColor: theme.hover, fontWeight: '600', fontSize: '0.8rem', color: theme.textSecondary, marginTop: primaryRoomChildren.length > 0 ? '8px' : 0 }}>
                          Available Visitors ({visitingChildren.length})
                        </div>
                        {visitingChildren.map(child => renderChild(child, true, false))}
                      </>
                    )}
                    {unscheduledChildren.length > 0 && (
                      <>
                        <div style={{ padding: '8px 12px', backgroundColor: theme.hover, fontWeight: '600', fontSize: '0.8rem', color: theme.textSecondary, marginTop: '8px' }}>
                          Not Scheduled This Day ({unscheduledChildren.length})
                        </div>
                        {unscheduledChildren.map(child => renderChild(child, false, true))}
                      </>
                    )}
                  </>
                );
              })()}
            </div>
            
            <div style={modalButtonRowStyle}>
              <button style={secondaryButtonStyle} onClick={() => setShowAssignModal(false)}>Cancel</button>
              <button style={primaryButtonStyle} onClick={saveAssignments}>Save ({selectedChildren.length})</button>
            </div>
          </div>
        </div>
      )}

      {/* Loading Overlay */}
      {isLoading && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000
        }}>
          <div style={{
            backgroundColor: theme.cardBackground,
            padding: '32px 48px',
            borderRadius: '12px',
            textAlign: 'center',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
          }}>
            <div style={{
              width: '48px',
              height: '48px',
              border: `4px solid ${theme.border}`,
              borderTopColor: colors.blue,
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 16px'
            }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            <div style={{ fontSize: '1.1rem', fontWeight: '600', color: theme.text }}>
              {loadingMessage || 'Processing...'}
            </div>
            <div style={{ fontSize: '0.85rem', color: theme.textSecondary, marginTop: '8px' }}>
              Please wait, this may take a moment
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ChildSchedule;
